/** @prettier */
// @flow

import { AuthorizationTypes } from '@site-builder/common/src/types/model/landing';
import {
  GK_TYPE,
  BUNDLE_TYPE,
  customTypesToStoreItem,
} from '@site-builder/common/src/utils/store';

import type { ScriptCommonArgs } from '../types';
import type { FiveSymbolLocale } from '@site-builder/common/src/utils/locale';

import { initCartButton } from '../../ui-components/Editor/CustomButton/Buttons/CartButton/initCartButton';
import { canShowCart, PlaceTypes } from '../../utils/cart-helper';
import { generateDrmList } from '../../utils/store-helper';
import { handleGameKey } from '../actions/buyGameKeys';
import { handleSubscription } from '../actions/buySubscription';
import { analyticsManager } from '../afterBlocks/analytics/analytics';
import { AnalyticsActions } from '../afterBlocks/analytics/analyticsActions';
import { UniversalCart } from '../blocks/store/cart/UniversalCart';
import { StoreAPIDataService } from '../blocks/store/services/StoreAPIDataService';
import subscribe from '../blocks/subscribe';
import { openUserIdModalForButton } from '../blocks/user-account-services';
import { getStore } from '../store';

export const initCartButtons = async ({
  storeAPIDataService,
  gameKey,
  button,
  cart,
  locale,
}: {|
  storeAPIDataService: StoreAPIDataService,
  gameKey: {
    sku: string,
    type: typeof GK_TYPE | typeof BUNDLE_TYPE,
    target: string,
  },
  cartModal: HTMLElement,
  button: HTMLElement,
  cart: UniversalCart,
  locale: FiveSymbolLocale,
|}) => {
  let storeData = storeAPIDataService.findBySku(gameKey.sku);

  if (!storeData) {
    await storeAPIDataService.loadLackItems([
      {
        ...customTypesToStoreItem(gameKey.type),
        sku: gameKey.sku,
      },
    ]);
    storeData = storeAPIDataService.findBySku(gameKey.sku);
  }
  if (!storeData) {
    throw new Error(
      `The store data was not found in the data storage when initializing cart buttons; SKU – ${gameKey.sku}`
    );
  }

  if (storeData.unit_items && storeData.unit_items.length === 1) {
    // eslint-disable-next-line prefer-destructuring
    storeData = storeData.unit_items[0];
  }

  const addToCartButtonWrapper =
    button.parentElement &&
    button.parentElement.querySelector('[data-cart-button-parent]');
  const checkoutButton =
    button.parentElement &&
    button.parentElement.querySelector(
      '[data-button-type="cart-modal-opener"]'
    );

  const selectorList = generateDrmList(storeData.unit_items || []);
  initCartButton({
    isServerSideRender: false,
    cart,
    storeAPIDataService,
    sku: storeData.sku,
    buyButton: button,
    addToCartButtonWrapper,
    checkoutButton,
    selectorList,
    isTextNeeded: false,
    locale,
  });
};

export const initBuyButtonsInBlock = ({
  analyticsCategory,
  block,
  data,
  translations,
}: $Shape<ScriptCommonArgs>) => {
  const cartModal = document.querySelector('[data-cart-modal]');
  if (!cartModal) {
    throw new Error('Cart element not found');
  }

  const store = getStore();
  const { landingServices, scripts } = store.getState();
  const { cart, storeAPIDataService, userService } = landingServices;
  const { auth } = scripts;

  /**
   * Initialization of buttons working through Xsolla Buy Button
   */
  block
    .querySelectorAll(
      'div.ui-site-calltoaction[data-button-type="gk"], div.ui-site-calltoaction[data-button-type="bundle"]'
    )
    .forEach(async (button, index) => {
      button.id = `${button.id}--${index}`; // TODO dirty hack to avoid collision ids

      const buttonSku = button.getAttribute('data-button-sku');
      const buttonType: typeof GK_TYPE | typeof BUNDLE_TYPE | typeof undefined =
        (button.getAttribute('data-button-type'): any);
      if (buttonSku && buttonType) {
        const gameKey = {
          sku: buttonSku,
          type: buttonType,
          target: `#${button.id}`,
        };

        button.addEventListener('click', () => {
          analyticsManager.sendEvent({
            category: analyticsCategory,
            event: AnalyticsActions.CLICK_BUY,
            page: `landing_${AnalyticsActions.CLICK_BUY}`,
            label: buttonSku,
          });
        });

        if (
          canShowCart({
            isMultipage: data.isMultipage,
            isNewScheme: data.isNewScheme,
            enableCart: data.cart.enable,
            placeType: PlaceTypes.CUSTOM_BUTTON,
          })
        ) {
          await initCartButtons({
            storeAPIDataService,
            gameKey,
            cartModal,
            button,
            cart,
            locale: data.locale,
          });
        }
        if (
          !userService.isAuthorized &&
          auth.type === AuthorizationTypes.USER_ID
        ) {
          button.onclick = (ev) => {
            ev.stopPropagation();
            const buyItem = button.closest('[data-buy-button-item]');
            openUserIdModalForButton(buyItem?.id);
          };
        }
        handleGameKey({ gameKey, data, storeAPIDataService });
      }
    });

  /**
   * Initialization of GooglePlay button
   */
  block
    .querySelectorAll(
      'a.ui-site-calltoaction[data-button-type="google_play_button"]'
    )
    .forEach((button, index) => {
      button.id = `${button.id}--${index}`; // TODO dirty hack to avoid collision ids
      button.addEventListener('click', () => {
        analyticsManager.sendEvent({
          category: analyticsCategory,
          event: AnalyticsActions.CLICK_GOOGLEPLAY,
          page: `landing_${AnalyticsActions.CLICK_GOOGLEPLAY}`,
        });
      });
    });

  /**
   * Initialization of AppStore button
   */
  block
    .querySelectorAll(
      'a.ui-site-calltoaction[data-button-type="apple_store_button"]'
    )
    .forEach((button, index) => {
      button.id = `${button.id}--${index}`; // TODO dirty hack to avoid collision ids
      button.addEventListener('click', () => {
        analyticsManager.sendEvent({
          category: analyticsCategory,
          event: AnalyticsActions.CLICK_APPSTORE,
          page: `landing_${AnalyticsActions.CLICK_APPSTORE}`,
        });
      });
    });

  /**
   * Initialization of buttons working directly through Pay Station
   */
  block
    .querySelectorAll(
      'div.ui-site-calltoaction[data-button-type="subscription"]'
    )
    .forEach((button, index) => {
      button.id = `${button.id}--${index}`; // TODO dirty hack to avoid collision ids

      const externalId = button.getAttribute('data-external-id');
      if (externalId) {
        handleSubscription({
          translations,
          data,
          target: `[id='${button.id}']`,
        });
        button.addEventListener('click', () => {
          analyticsManager.sendEvent({
            category: analyticsCategory,
            event: AnalyticsActions.CLICK_BUY,
            page: `landing_${AnalyticsActions.CLICK_BUY}`,
            label: externalId,
          });
        });
      }
    });
};

/**
 * Initialization of news subscription handlers
 */
export const initSubscribing = ({
  analyticsCategory,
  block,
  data: { subscriptions, domain, projectId, googleAnalytics },
}: ScriptCommonArgs) => {
  block.querySelectorAll('.ui-site-subscription').forEach((sub, idx) => {
    sub.id = `${sub.id}--${idx}`;
    const currentSub = subscriptions.find((s) =>
      `${sub.id}`.includes(s.elementId)
    );
    if (!currentSub) {
      return;
    }
    const newSub = { ...currentSub };
    newSub.elementId = `${sub.id}`;
    subscribe({
      subscription: newSub,
      domain,
      projectId,
      googleAnalytics,
    });

    sub.addEventListener('submit', () => {
      analyticsManager.sendEvent({
        category: analyticsCategory,
        event: AnalyticsActions.ENTER_EMAIL,
        page: 'landing_input-subscribe-confirm',
      });
    });
  });
};
